import React from "react"
import IconsColor from "util/iconsColor"
import { getImportRowPreview, IAgencyImportIssueRow } from "./agencyImportUtil"
import styles from "./importAgencyPopup.module.scss"

export type ImportIssueSection = "duplicate" | "missingOrInvalid"

type ImportIssueSectionConfig = {
    title: string
    detailLabel: string
    iconName: string
    iconColor: string
    variantClassName: string
    drawerDescription: (count: number) => string
}

const recordsLabel = (count: number): string => `${count} record${count === 1 ? "" : "s"}`

export const IMPORT_ISSUE_SECTIONS: Record<ImportIssueSection, ImportIssueSectionConfig> = {
    duplicate: {
        title: "Duplicate Records",
        detailLabel: "Reason",
        iconName: "copyIcon",
        iconColor: "#B45309",
        variantClassName: styles.sectionWarning,
        drawerDescription: (count) =>
            `${recordsLabel(count)} already exist in the system or repeat an earlier row, and were skipped during import.`,
    },
    missingOrInvalid: {
        title: "Records with missing or invalid fields",
        detailLabel: "Missing/Invalid Format",
        iconName: "warningIcon",
        iconColor: "#DC2626",
        variantClassName: styles.sectionDanger,
        drawerDescription: (count) =>
            `${recordsLabel(count)} had a blank required field or an invalid format, and were skipped during import.`,
    },
}

const SKIPPED_SUBTITLE = "These were skipped during import"

type ImportSummarySectionProps = {
    section: ImportIssueSection
    count: number
    rows: IAgencyImportIssueRow[]
    expanded: boolean
    onToggle: () => void
    onViewAll: () => void
}

const ImportSummarySection: React.FC<ImportSummarySectionProps> = ({
    section,
    count,
    rows,
    expanded,
    onToggle,
    onViewAll,
}) => {

    const config = IMPORT_ISSUE_SECTIONS[section]
    const isExpandable = rows.length > 0
    const isOpen = isExpandable && expanded
    const preview = getImportRowPreview(rows)

    const headerContent = (
        <>
            <span className={styles.sectionIcon}>
                <IconsColor iconName={config.iconName} width="20" height="20" fillColor="none" strokeColor={config.iconColor} />
            </span>
            <span className={styles.sectionText}>
                <span className={styles.sectionTitle}>{config.title}</span>
                <span className={styles.sectionSubtitle}>{SKIPPED_SUBTITLE}</span>
            </span>
            <span className={styles.sectionCount}>{count}</span>
            {isExpandable && (
                <span className={`${styles.sectionChevron}${isOpen ? ` ${styles.sectionChevronOpen}` : ""}`}>
                    <IconsColor iconName="chevronDown" width="18" height="18" fillColor="none" strokeColor="currentColor" />
                </span>
            )}
        </>
    )

    return (
        <div className={`${styles.section} ${config.variantClassName}`}>
            {isExpandable ? (
                <button type="button" className={styles.sectionHeader} onClick={onToggle} aria-expanded={isOpen}>
                    {headerContent}
                </button>
            ) : (
                <div className={styles.sectionHeader}>{headerContent}</div>
            )}

            {isOpen && (
                <div className={styles.sectionBody}>
                    <table className={styles.sectionTable}>
                        <thead>
                            <tr>
                                <th>Row</th>
                                <th>Agency</th>
                                <th>Organization</th>
                                <th>{config.detailLabel}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preview.rows.map((row, index) => (
                                <tr key={`${row.row}-${index}`}>
                                    <td className={styles.sectionRowNumber}>{row.row || "-"}</td>
                                    <td>{row.agency_name || "-"}</td>
                                    <td>
                                        <span className={styles.cellPrimary}>{row.office_name || "-"}</span>
                                        <span className={styles.cellSecondary}>{row.agent_name || "-"}</span>
                                    </td>
                                    <td>{row.detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {preview.hiddenCount > 0 && (
                        <button type="button" className={styles.sectionMoreButton} onClick={onViewAll}>
                            …and {preview.hiddenCount} more (click to view all)
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default ImportSummarySection
